/**
 * Shared answer-text normalization and fuzzy-distance utilities.
 *
 * Used by every text-comparison exercise type (FillInBlank, Scramble, …) so that
 * normalization rules stay consistent in one place. The motivating bug: a student
 * answer with a straight apostrophe ("haven't") failed to match an expected answer
 * stored with a curly apostrophe ("haven’t"), producing a misleading "Almost there".
 */

// Single-quote / apostrophe variants → straight ' :  ‘ ’ ʼ ′ ` ´
const SINGLE_QUOTES = /[‘’ʼ′`´]/g;
// Double-quote variants → straight " :  “ ” ″
const DOUBLE_QUOTES = /[“”″]/g;
// Hyphen / dash variants → '-' :  ‐ ‑ ‒ – — ― and the math minus −
const DASHES = /[‐-―−]/g;
// Unicode combining diacritical marks (after NFD decomposition)
const COMBINING_MARKS = /[̀-ͯ]/g;

export interface NormalizeAnswerOptions {
  /** Expand common English contractions (don't → do not). Default: true. */
  expandContractions?: boolean;
  /** Fold diacritics so "café" === "cafe". Default: true. */
  foldDiacritics?: boolean;
  /**
   * Ignore sentence punctuation differences: strips `.` and `,`, removes spacing
   * before `? ! ; :` ("word ?" === "word?") and drops trailing `? ! ; :`
   * ("word?" === "word"). Default: true.
   */
  stripPunctuation?: boolean;
}

/**
 * Expand the common, unambiguous English contractions.
 *
 * Applied to both expected and student answers, so it can only ever ADD matches,
 * never remove them. Ambiguous forms ('s = is/has/possessive, 'd = had/would) are
 * deliberately left untouched to avoid surprising equivalences.
 */
function expandEnglishContractions(text: string): string {
  return text
    .replace(/\bwon't\b/g, 'will not')
    .replace(/\bcan't\b/g, 'can not')
    .replace(/\bshan't\b/g, 'shall not')
    .replace(/n't\b/g, ' not') // haven't → have not, doesn't → does not
    .replace(/'m\b/g, ' am') // i'm → i am
    .replace(/'re\b/g, ' are') // you're → you are
    .replace(/'ve\b/g, ' have') // we've → we have
    .replace(/'ll\b/g, ' will') // they'll → they will
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize an answer string for comparison.
 *
 * Pipeline: NFC → unify quote/dash glyphs → (optional) fold diacritics →
 * collapse whitespace → trim → lowercase → (optional) expand contractions →
 * (optional) strip `.`/`,`, spacing before `?!;:` and trailing `?!;:`.
 */
export function normalizeAnswer(
  value: string | undefined | null,
  options: NormalizeAnswerOptions = {},
): string {
  const {
    expandContractions = true,
    foldDiacritics = true,
    stripPunctuation = true,
  } = options;

  let result = (value ?? '')
    .normalize('NFC')
    .replace(SINGLE_QUOTES, "'")
    .replace(DOUBLE_QUOTES, '"')
    .replace(DASHES, '-');

  if (foldDiacritics) {
    result = result.normalize('NFD').replace(COMBINING_MARKS, '');
  }

  result = result.replace(/\s+/g, ' ').trim().toLocaleLowerCase();

  if (expandContractions) {
    result = expandEnglishContractions(result);
  }

  if (stripPunctuation) {
    result = result
      .replace(/[.,]/g, '')
      // "word ?" and "word?" are the same answer — students often leave a space
      // before a question or exclamation mark.
      .replace(/\s+([?!;:])/g, '$1')
      // A missing or extra closing mark is not a wrong answer either.
      .replace(/[?!;:]+$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return result;
}

/**
 * Damerau–Levenshtein (optimal string alignment) edit distance.
 *
 * Like Levenshtein but counts an adjacent-character transposition ("hte" → "the")
 * as a single edit, which better matches real human typos.
 */
export function damerauLevenshtein(a: string, b: string): number {
  const s = a ?? '';
  const t = b ?? '';
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;

  const rows = s.length + 1;
  const cols = t.length + 1;
  const d: number[][] = Array.from({ length: rows }, () => new Array(cols).fill(0));

  for (let i = 0; i < rows; i += 1) d[i][0] = i;
  for (let j = 0; j < cols; j += 1) d[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1, // deletion
        d[i][j - 1] + 1, // insertion
        d[i - 1][j - 1] + cost, // substitution
      );
      if (i > 1 && j > 1 && s[i - 1] === t[j - 2] && s[i - 2] === t[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1); // transposition
      }
    }
  }

  return d[s.length][t.length];
}

/**
 * Edit distance normalized by the longer of the two strings (0 = identical, 1 = fully different).
 * Lets callers apply a length-relative typo threshold instead of an absolute one — a 1-char
 * slip matters far more in a 3-letter word than in a 14-letter word.
 */
export function relativeEditDistance(a: string, b: string): number {
  const longest = Math.max(a.length, b.length);
  if (longest === 0) return 0;
  return damerauLevenshtein(a, b) / longest;
}
